import * as React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CustomerMigrationUpload from './CustomerMigrationUpload';
import CustomerMigrationHistory from './CustomerMigrationHistory';
import DeferredMessages from './DeferredMessages';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import FilterListIcon from '@mui/icons-material/FilterList';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import './IHubAdapter.scss';

function TabPanel(props: {
  children?: React.ReactNode;
  value: number;
  index: number;
  [key: string]: unknown;
}) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
      style={{ flex: 1, minHeight: 0, display: value === index ? 'flex' : 'none' }}
    >
      {value === index && (
        <Box p={2} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function IHubAdapter() {
  const [value, setValue] = React.useState(0);
  const [migratedCustomer, setMigratedCustomer] = React.useState<string | null>(null);

  const toggleFiltersRef = React.useRef<() => void>(() => {});
  const toggleFullScreenRef = React.useRef<() => void>(() => {});
  const copyServiceIdsRef = React.useRef<() => void>(() => {});
  const [selectedCount, setSelectedCount] = React.useState(0);
  const [copyableCount, setCopyableCount] = React.useState(0);

  const handleSelectionChange = React.useCallback((count: number, copyable: number) => {
    setSelectedCount(count);
    setCopyableCount(copyable);
  }, []);

  const handleDeferredMount = React.useCallback(
    (actions: {
      toggleFilters: () => void;
      toggleFullScreen: () => void;
      copySelectedServiceIds: () => void;
    }) => {
      toggleFiltersRef.current = actions.toggleFilters;
      toggleFullScreenRef.current = actions.toggleFullScreen;
      copyServiceIdsRef.current = actions.copySelectedServiceIds;
    },
    []
  );

  const handleJobStarted = (customerNumber: string) => {
    setMigratedCustomer(customerNumber);
    setValue(1);
  };

  return (
    <div className="ihub-container" id="ihub-adapter-container">
      <div className="ihub-tab-wrapper">
        <Tabs
          value={value}
          onChange={(_e, v) => setValue(v)}
          TabIndicatorProps={{ style: { backgroundColor: 'var(--color-primary)' } }}
          id="ihub-adapter-tabs"
          aria-label="iHub adapter navigation"
        >
          <Tab
            icon={<CloudUploadIcon fontSize="small" />}
            iconPosition="start"
            label="Bulk Migration"
            className={value === 0 ? 'ihub-tab-selected' : 'ihub-tab-unselected'}
            id="ihub-adapter-upload-tab"
            aria-controls="ihub-adapter-upload-panel"
          />
          <Tab
            icon={<AssessmentIcon fontSize="small" />}
            iconPosition="start"
            label="Migration History"
            className={value === 1 ? 'ihub-tab-selected' : 'ihub-tab-unselected'}
            id="ihub-adapter-history-tab"
            aria-controls="ihub-adapter-history-panel"
          />
          <Tab
            icon={<PendingActionsIcon fontSize="small" />}
            iconPosition="start"
            label="Deferred Messages"
            className={value === 2 ? 'ihub-tab-selected' : 'ihub-tab-unselected'}
            id="ihub-adapter-deferred-tab"
            aria-controls="ihub-adapter-deferred-panel"
          />
        </Tabs>

        {value === 2 && (
          <div className="ihub-dm-actions">
            {selectedCount > 0 && (
              <Tooltip
                title={
                  copyableCount > 0
                    ? 'Copy selected service IDs'
                    : 'Selected rows have no service IDs to copy'
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    className="ihub-dm-copy-btn"
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    disabled={copyableCount === 0}
                    onClick={() => copyServiceIdsRef.current?.()}
                  >
                    Copy IDs ({copyableCount})
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Toggle filters">
              <IconButton
                size="small"
                className="ihub-dm-action-btn"
                aria-label="Toggle filters"
                onClick={() => toggleFiltersRef.current?.()}
              >
                <FilterListIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fullscreen">
              <IconButton
                size="small"
                className="ihub-dm-action-btn"
                aria-label="Fullscreen"
                onClick={() => toggleFullScreenRef.current?.()}
              >
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>

      <TabPanel value={value} index={0} id="ihub-adapter-upload-panel">
        <CustomerMigrationUpload onJobStarted={handleJobStarted} />
      </TabPanel>
      <TabPanel value={value} index={1} id="ihub-adapter-history-panel">
        <CustomerMigrationHistory initialCustomer={migratedCustomer} />
      </TabPanel>
      <TabPanel value={value} index={2} id="ihub-adapter-deferred-panel">
        <DeferredMessages onMount={handleDeferredMount} onSelectionChange={handleSelectionChange} />
      </TabPanel>
    </div>
  );
}
